import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { FaPlus, FaDownload } from 'react-icons/fa';
import { RxCrossCircled } from 'react-icons/rx';
import { useParams } from 'react-router-dom';
import { ExpenseItem, TransactionUpload } from '~/types/fundrequests';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import { deleteFundRequestItemUpload } from '~/services/fund-requests';
import { useAuth } from '~/context/AuthProvider';
import DeleteUploadConfirmationModal from '~/components/FundRequests/modals/DeleteUploadConfirmationModal';
import JSZip from 'jszip';

import imageCompression from 'browser-image-compression';

// Type definitions for pending uploads
interface PendingUpload {
    file: File;
    originalName: string;
    size: number;
    type: string;
    preview: string;
}

type Props = {
    expenseItem: ExpenseItem | undefined;
    setSlideIndex?: (slideIndex: number) => void;
    selectedIndex?: number;
};

// Type guard for checking if expenseItem has uploads property
const hasUploads = (item: any): item is { uploads: TransactionUpload[] } => {
    return item && 'uploads' in item && Array.isArray(item.uploads);
};

// Target size (in MB) used by browser-image-compression when shrinking uploaded images.
const IMAGE_COMPRESSION_TARGET_MB = 5;

const UploadDocument = ({
    expenseItem,
    setSlideIndex = () => {}, // Default no-op function
    selectedIndex,
}: Props) => {
    const { formKey, pendingUploads, fundRequestDispatch, fundRequest, uploadPath } = useFundRequestContext();
    const { person } = useAuth();
    const [uploadedFiles, setUploadedFiles] = useState<TransactionUpload[]>([]);
    const [error, setError] = useState('');
    const {
        formState: { errors },
    } = useFormContext();
    const { id } = useParams();

    // Get existing uploads from the expense item if available
    const uploads = hasUploads(expenseItem) ? expenseItem.uploads : [];

    useEffect(() => {
        if (uploads && uploads.length > 0) {
            setUploadedFiles(uploads);
        }
    }, [uploads]);

    const resizeImage = useCallback(async (file: File) => {
        const options = {
            maxSizeMB: IMAGE_COMPRESSION_TARGET_MB,
            maxWidthOrHeight: 2000,
            useWebWorker: true,
        };
        try {
            const compressedFile = await imageCompression(file, options);
            return compressedFile;
        } catch (error) {
            console.error('Error compressing image:', error);
            // Return the original file if compression fails
            return file;
        }
    }, []);

    const onDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: any[]) => {
            if (fileRejections.length > 0) {
                setError('File must be an image or PDF');
                return;
            }
            if (!id || !fundRequest) {
                setError('Invalid fund request id');
                return;
            }
            if (!formKey) {
                setError('No form key set - please select an expense first');
                return;
            }

            setError('');

            // Process each file
            for (const file of acceptedFiles) {
                try {
                    console.log('Processing file:', file.name);

                    let processedFile = file;

                    // Only compress images, not PDFs
                    if (file.type.startsWith('image/')) {
                        processedFile = await resizeImage(file);
                        console.log('Image resized successfully');
                    } else {
                        console.log('PDF file - skipping compression');
                    }

                    // Generate a preview URL
                    const preview = URL.createObjectURL(processedFile);
                    console.log('Preview URL created');

                    // Store the file in the context
                    fundRequestDispatch({
                        type: 'add_pending_upload',
                        payload: {
                            formKey: formKey,
                            file: {
                                file: processedFile,
                                originalName: file.name,
                                size: processedFile.size,
                                type: processedFile.type,
                                preview: preview
                            }
                        }
                    });

                    console.log('File added to pending uploads');
                } catch (error) {
                    console.error('Error processing file:', error);
                    setError('Error processing file. Please try again.');
                }
            }
        },
        [id, formKey, fundRequestDispatch, resizeImage, fundRequest]
    );

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png'],
            'application/pdf': ['.pdf'],
        },
        multiple: false,
        noClick: true,
    });

    // Get pending uploads for the current form key
    const currentPendingUploads: PendingUpload[] = pendingUploads?.[formKey] || [];

    const removePendingUpload = (index: number) => {
        if (!formKey) return;

        fundRequestDispatch({
            type: 'remove_pending_upload',
            payload: {
                formKey,
                index
            }
        });
    };

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedUpload, setSelectedUpload] = useState<TransactionUpload | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const canDeleteUploads = Boolean(
        fundRequest?.created_by?.id && person?.id && fundRequest.created_by.id === person.id
    );

    const requestId = fundRequest?.id;

    const handleRequestDeleteUpload = (upload: TransactionUpload) => {
        if (!canDeleteUploads) return;
        setSelectedUpload(upload);
        setConfirmOpen(true);
    };

    const confirmDeleteUpload = async () => {
        if (!selectedUpload || !requestId || !formKey) {
            setConfirmOpen(false);
            return;
        }
        try {
            setIsDeleting(true);
            if (!uploadPath) {
                throw new Error('Upload path is not set');
            }
            await deleteFundRequestItemUpload(uploadPath, selectedUpload.id);

            // Update local list
            setUploadedFiles(prev => prev.filter(f => f.id !== selectedUpload.id));
            setConfirmOpen(false);
            setSelectedUpload(null);
        } catch (error) {
            console.error('Failed to delete upload', error);
            setConfirmOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const [isDownloadingAll, setIsDownloadingAll] = useState(false);

    const downloadAllDocuments = async () => {
        if (uploadedFiles.length === 0) {
            return;
        }

        setIsDownloadingAll(true);
        
        try {
            const zip = new JSZip();
            const promises: Promise<void>[] = [];

            // Add each document to the zip
            uploadedFiles.forEach((upload, index) => {
                const promise = fetch(upload.image_url)
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to download ${upload.filename}`);
                        }
                        return response.blob();
                    })
                    .then(blob => {
                        // Create a unique filename to avoid duplicates
                        const extension = upload.filename?.split('.').pop() || '';
                        const nameWithoutExt = upload.filename?.replace(/\.[^/.]+$/, '') || `document_${index + 1}`;
                        const uniqueFilename = `${nameWithoutExt}_${index + 1}.${extension}`;
                        
                        zip.file(uniqueFilename, blob);
                    })
                    .catch(error => {
                        console.error(`Error downloading ${upload.filename}:`, error);
                        // Add a text file noting the error instead
                        zip.file(`ERROR_${upload.filename || `document_${index + 1}`}.txt`, 
                                 `Failed to download this file: ${error.message}`);
                    });

                promises.push(promise);
            });

            // Wait for all downloads to complete
            await Promise.all(promises);

            // Generate the zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(zipBlob);
            link.href = url;
            
            // Include fund request ID in filename if available
            const fundRequestId = id || fundRequest?.id;
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = fundRequestId 
                ? `fundrequest_${fundRequestId}_documents_${dateStr}.zip`
                : `expense_documents_${dateStr}.zip`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error creating zip file:', error);
            setError('Failed to download documents. Please try again.');
        } finally {
            setIsDownloadingAll(false);
        }
    };

    if (!formKey) {
        return (
            <div className="text-center p-4">
                <p className="text-gray-500">Please select an expense to upload documents.</p>
            </div>
        );
    }

    return (
        <>
            <label className='font-semibold'>Add Supporting Documents</label>
            <div className='border-gray-400 bg-gray-100 cursor-pointer rounded-lg border-2 border-dashed p-5 text-center w-full'>
                <div
                    {...getRootProps()}
                    className='flex flex-col items-center justify-center gap-2 w-full'
                >
                    <input {...getInputProps()} />
                    <div className='flex items-center gap-8'>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                open();
                            }}
                            className='flex items-center gap-[8px] rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                        >
                            <FaPlus className='text-[16px]' />
                            Upload New
                        </button>
                    </div>
                </div>
                {error && (
                    <div className='text-red-600 mt-2 text-sm'>{error}</div>
                )}
                {errors?.files && (
                    <div className='text-red-600 mt-2 text-sm'>
                        {String(errors?.files?.message || 'File error')}
                    </div>
                )}

                {/* Display pending uploads */}
                {currentPendingUploads.length > 0 && (
                    <div className="mt-3 w-full">
                        <h3 className="text-sm font-semibold mb-1">Pending Uploads:</h3>
                        <ul className='w-full'>
                            {currentPendingUploads.map((upload: PendingUpload, index: number) => (
                                <li key={`pending-${index}`} className="mb-1">
                                    <div className='inline-flex items-center gap-2 rounded-full bg-[#f0f0f0] px-2 text-[12px]'>
                                        <span className="truncate max-w-[300px]" title={upload.originalName}>{upload.originalName}</span>
                                        <RxCrossCircled
                                            className='cursor-pointer'
                                            onClick={() => removePendingUpload(index)}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Display existing uploads */}
                {uploadedFiles.length > 0 && (
                    <div className="mt-3 w-full">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="text-sm font-semibold">Existing Files:</h3>
                            <button
                                onClick={downloadAllDocuments}
                                disabled={isDownloadingAll}
                                className={`flex items-center gap-[8px] rounded-full px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200 ${
                                    isDownloadingAll 
                                        ? 'text-gray-400 cursor-not-allowed' 
                                        : 'text-[#00AEB3] hover:bg-[#e6f7f8]'
                                }`}
                                title="Download all documents as ZIP"
                            >
                                {isDownloadingAll ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                        <span>Downloading...</span>
                                    </>
                                ) : (
                                    <>
                                        <FaDownload className='text-[16px]' />
                                        <span>Download All</span>
                                    </>
                                )}
                            </button>
                        </div>
                        <ul className='w-full'>
                            {uploadedFiles.map((upload: TransactionUpload, index: number) => {
                                const isSelected = selectedIndex === index;
                                return (
                                    <li key={`existing-${index}`} className="mb-1">
                                        <div className={`inline-flex items-center gap-2 rounded-full px-2 text-[12px] transition-colors ${
                                            isSelected 
                                                ? 'bg-blue-100 border-2 border-blue-400 shadow-sm' 
                                                : 'bg-[#e6f7f8] hover:bg-[#d1f2f4]'
                                        }`}>
                                            <span
                                                onClick={() => {
                                                    setSlideIndex(index);
                                                }}
                                                className="truncate max-w-[300px] cursor-pointer"
                                                title={upload.filename}
                                            >
                                                {upload.filename}
                                            </span>
                                            {canDeleteUploads && (
                                                <RxCrossCircled
                                                    className='cursor-pointer'
                                                    onClick={() => handleRequestDeleteUpload(upload)}
                                                />
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
            <DeleteUploadConfirmationModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmDeleteUpload}
                isLoading={isDeleting}
                filename={selectedUpload?.filename}
            />
        </>
    );
};

export default UploadDocument;
